import { html, render } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { appInfo } from '@polleverywhere/slideware-bindings/lib/app_info'
import Backbone from '@polleverywhere/backbone'
import showReportTab from '../../util/show_report_tab'
import _ from 'underscore'
import '../tab_bar'
import './style.scss'

function hiddenTabBarItems (profile) {
  if (profile === 'presenter') {
    return !showReportTab(LayoutTabBar.user, LayoutTabBar.mode,
      LayoutTabBar.allowRedirects) ? 'reports' : undefined
  }

  if (profile === 'agave') {
    return 'reports'
  }

  if (profile === 'participant') {
    const items = []

    const os = appInfo().operatingSystem
    const isMobileDevice = os === 'android' || os === 'ios' || window.Capacitor?.isNative
    if (isMobileDevice) {
      items.push('present')
    }

    // is logged in
    if (LayoutTabBar.user && LayoutTabBar.user.get('id')) {
      items.push('log in')

      const isParticipantOnly = LayoutTabBar.user?.isOnlyRegParticipant() ?? true
      if (!isParticipantOnly) {
        items.push('present')
      }
    } else {
      items.push('settings')
    }

    return _.uniq(items).join(',')
  }

  return ''
}

function tabBarTemplate (tabBar = {}) {
  return html`
    <tab-bar profile='${ifDefined(tabBar.profile)}'
      active='${ifDefined(tabBar.active)}'
      hide='${ifDefined(tabBar.hide)}'
    ></tab-bar>
  `
}

function template (content, tabBar = {}) {
  return html`
    ${tabBarTemplate(tabBar)}
    <section>
      <div name='content-wrapper'>
        ${content}
      </div>
    </section>
  `
}

function renderTemplate () {
  if (!this.isConnected) return
  const tabBar = {
    profile: this.getAttribute('profile') || undefined,
    active: this.getAttribute('active') || undefined,
    hide: hiddenTabBarItems(this.getAttribute('profile'))
  }

  render(template(this.slots('content')[0], tabBar), this)
}

/**
 * Layout element contains a single `<tab-bar>` element and a content slot that
 * is declared using a slot attribute, e.g. `<div slot='content'>`.
 *
 * @element layout-tab-bar
 *
 * @fires change:active - From <tab-bar>
 * @fires click:tab - From <tab-bar>
 *
 * @attr {string} profile - Set `tab-bar` profile initials
 * @attr {string} active - Set `tab-bar` active tab
 * @attr {string} hide - Comma separated list of tabs to hide in the `tab-bar`.
 * @attr {string} role - Role
 *
 * @slot content - HTML element or document fragment to insert as content
 *
 * @cssprop --background-color - Set background color
 * @cssprop --color - Set foreground color
 * @cssprop --content-height - Set a height on the content slot
 * @cssprop --content-max-width - Set a max width around the content container
 * @cssprop --content-padding - Set padding around the content container. If
 *  overridden, directional padding vars will become ineffective unless they are
 *  included as part of the value, e.g.
 *  `--content-padding: var(--content-padding-top, 1rem);`
 * @cssprop --content-padding-top - Set top padding around content.
 * @cssprop --content-padding-right - Set right padding around content.
 * @cssprop --content-padding-bottom - Set bottom padding around content.
 * @cssprop --content-padding-left - Set left padding around content.
 */
export default class LayoutTabBar extends HTMLElement {
  static get tagName () {
    return 'layout-tab-bar'
  }

  static get observedAttributes () {
    return ['active', 'profile', 'hide']
  }

  static set user (user) {
    this._user = user
  }

  static get user () {
    return this._user
  }

  static set mode (mode) {
    this._mode = mode
  }

  static get mode () {
    return this._mode
  }

  constructor () {
    super()
    this.listener = _.clone(Backbone.Events)
    this.listener.listenTo(LayoutTabBar.user, 'change', renderTemplate.bind(this))
  }

  connectedCallback () {
    defineElementSlot(this, 'content')

    renderTemplate.call(this)
  }

  attributeChangedCallback () {
    renderTemplate.call(this)
  }

  disconnectedCallback () {
    this.listener.stopListening()
  }
}

register(module, LayoutTabBar.tagName, LayoutTabBar)
